import { useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $userAccountsStore, $userCardsStore } from '@lib/store';
import { cn, FormatUtils } from '@shared/utils';
import { AccountType, type UserResponse } from '@shared/api';
import { Button, Icon, Modal, Text } from '@shared/widgets';
import CrossIcon from '@assets/icons/cross.svg';

interface RemoveCardsProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserResponse;
}

export const RemoveCardsModal = (props: RemoveCardsProps) => {
  const userAccountsStore = useUnit($userAccountsStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  const [selectedAccounts, setSelectedAccounts] = useState<number[]>([]);
  const [selectedCards, setSelectedCards] = useState<number[]>([]);

  const { t } = useTranslation('user');

  const closeHandler = () => {
    setSelectedAccounts([]);
    setSelectedCards([]);
    props.onClose();
  };
  const confirmRemoveHandler = async () => {
    if (!selectedCards.length && !selectedAccounts.length) {
      closeHandler();
      return;
    }
    await Promise.allSettled([
      selectedAccounts.length > 0 && $userAccountsStore.events.deleteAccount({ userIds: props.user.id, accountIds: selectedAccounts }),
      selectedCards.length > 0 && $userCardsStore.events.deleteCard({ userIds: props.user.id, cardIds: selectedCards }),
    ]);
    closeHandler();
  };

  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title='Remove Payment Accounts and Cards'>
      <div className='flex flex-col gap-2'>
        {userAccountsStore.data.map((account) => (
          <div
            key={account.id}
            className={cn(
              'flex w-full items-center justify-between rounded-sm bg-white p-2 pl-3 dark:bg-slate',
              selectedAccounts.includes(account.id) && 'pointer-events-none opacity-50',
            )}
          >
            <div className='shrink pr-4'>
              <Text className='text-ellipsis' size='sm'>
                {account.type === AccountType.Payment ? account.accountNumber : FormatUtils.toSliceNumber(account.accountNumber)}
              </Text>
              <Text className='text-ellipsis' size='xs' color='gray'>
                {account.productName}
              </Text>
            </div>
            <Button className='p-0' color='icon' onClick={() => setSelectedAccounts((prev) => [...prev, account.id])}>
              <Icon icon={<CrossIcon className='h-6 w-6' />} color='red' />
            </Button>
          </div>
        ))}
        {userCardsStore.data.data.map((card) => (
          <div
            key={card.cardId}
            className={cn(
              'flex w-full items-center justify-between rounded-sm bg-white p-2 pl-3 dark:bg-slate',
              selectedCards.includes(card.cardId) && 'pointer-events-none opacity-50',
            )}
          >
            <div className='shrink pr-4'>
              <Text className='text-ellipsis' size='sm'>
                {FormatUtils.toSliceNumber(card.cardNumber, 16)}
              </Text>
              <Text className='text-ellipsis' size='xs' color='gray'>
                {card.cardHolder}
              </Text>
            </div>
            <Button className='p-0' color='icon' onClick={() => setSelectedCards((prev) => [...prev, card.cardId])}>
              <Icon icon={<CrossIcon className='h-6 w-6' />} color='red' />
            </Button>
          </div>
        ))}
      </div>
      <Button className='mt-5' color='primary' fullWidth disabled={userCardsStore.loading} onClick={confirmRemoveHandler}>
        {t('buttons.confirm', { ns: 'common' })}
      </Button>
    </Modal>
  );
};
